import { cancelInvoice, selectCurrentPage, selectInvoices, selectInvoicesLoading, selectTotalPages, setPage } from "@/entities/invoice"
import { getInvoicePdf, IInvoice, InvoiceStatus, InvoiceStatuses } from "@/shared/api/invoices"
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { ActionButton } from "@/shared/ui/action-button"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Pagination } from "@/shared/ui/pagination"
import { Table } from "@/shared/ui/table"
import { ColumnDef } from "@tanstack/react-table"
import DownloadSvg from "@/shared/assets/images/CloudArrowUp.svg"
import notify from "@/shared/lib/notify"
import TrashSvg from "@/shared/assets/images/Trash.svg"

const invoiceStatuses: Record<InvoiceStatus, { text: string, color: string }> = {
    pending: {
        text: "Обрабатывается",
        color: "text-orange-400"
    },
    rejected: {
        text: "Отклонен",
        color: "text-red-500"
    },
    approved: {
        text: "Одобрен",
        color: "text-color-primary"
    },
    canceled: {
        text: "Отменен",
        color: "text-color-dark"
    },
}

export const InvoicesTable = () => {
    const invoices = useAppSelector(selectInvoices)
    const isLoading = useAppSelector(selectInvoicesLoading)
    const page = useAppSelector(selectCurrentPage)
    const totalPages = useAppSelector(selectTotalPages)
    const dispatch = useAppDispatch()

    const handleDownload = async (id: number) => {
        await getInvoicePdf(id)
        .then((resp) => {
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute(
                'target',
                `_blank`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        })
        .catch(() => {
            notify.error("Не удалось скачать счёт")
        })
    }

    const columns: ColumnDef<IInvoice, any>[] = [
        {
            accessorFn: (invoice) => invoice,
            cell: (info) => {
                const invoice: IInvoice = info.getValue()
                return (
                    <div className='text-base text-color-dark text-wrap overflow-hidden text-ellipsis max-w-[40vw] inline-block break-words'>
                        {invoice.document_number}
                    </div>
                )
            },
            id: 'no',
            enableSorting: false,
            header: () => <div className="!text-start">Номер счета</div>,
        },
        {
            accessorFn: (invoice) => invoice,
            cell: (info) => {
                const invoice: IInvoice = info.getValue()
                return (
                    <div className='text-base text-color-dark text-wrap overflow-hidden text-ellipsis max-w-[40vw] inline-block break-words'>
                        {invoice.amount} ₽
                    </div>
                )
            },
            id: 'amount',
            enableSorting: false,
            header: () => <div className="!text-start w-min">Сумма</div>,
        },
        {
            accessorFn: (invoice) => invoice,
            cell: (info) => {
                const invoice: IInvoice = info.getValue()
                const status = invoiceStatuses[invoice.status]
                return (
                    <div className={`text-base text-wrap overflow-hidden text-ellipsis max-w-[40vw] inline-block break-words ${status.color}`}>
                        {status.text}
                    </div>
                )
            },
            id: 'status',
            enableSorting: false,
            header: () => <div className="!text-start w-min">Статус</div>,
        },
        {
            accessorFn: (invoice) => (
                <div className="text-nowrap">
                    {toLocaleDateTimeString(invoice.created_at)}
                </div>
            ),
            id: 'date',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Дата создания</div>,
        },
        {
            accessorFn: (invoice) => invoice,
            enableSorting: false,
            id: 'actions',
            cell: info => {
                const invoice: IInvoice = info.getValue()
                return (
                    <div className="w-full flex justify-end gap-2">
                        {invoice.status === InvoiceStatuses.pending &&
                            <ActionButton
                                onClick={() => dispatch(cancelInvoice(invoice.id))}
                                icon={TrashSvg}
                            />  
                        }
                        <ActionButton
                            onClick={() => handleDownload(invoice.id)}
                            icon={DownloadSvg}
                        />
                    </div>
                )
            },
            header: () => <div />,
        }
    ]
    return (
        <>
            {Boolean(invoices.length) &&
                <div className="table_box">
                    <Table columns={columns} data={invoices} />
                    <Pagination 
                        page={page} 
                        totalPages={totalPages} 
                        setPage={(page) => setPage(page)}
                    />
                </div>
            }
            {(!Boolean(invoices.length) && isLoading) &&
                <div className="info_panel">
                    <Loader />
                </div>
            }
            {(!Boolean(invoices.length) && !isLoading) &&
                <NoTableData label='Нет активных счетов'/>
            }
        </>
    )
}